import { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import Swal from 'sweetalert2';
import {
  CCard,
  CCol,
  CRow,
  CBadge,
  CProgress,
  CProgressBar,
  CSpinner,
  CButton,
} from '@coreui/react';
import { CircularProgressbarWithChildren } from 'react-circular-progressbar';
import 'react-circular-progressbar/dist/styles.css';
import './ProjectDetails.css';
import { MultiSelect } from 'react-multi-select-component';
import ThingsToConsider from '../aimhi-widgets/ThingToConsider';
import $axios from 'src/utils/axios';
import IP from '../../StoreGlobal/IP';
import Notifications from '../../websocket/notifications';
import { useSelector, useDispatch } from 'react-redux';
import NumberFormat from 'react-number-format';
import Loading from '../aimhi-widgets/Loading';
import { requestSowNotifications } from 'src/redux/actions/notifications';
import useSubscriptionCheck from 'src/utils/useSubscriptionCheck';
import { useDialog } from 'src/utils/DialogContext';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import {
  DownloadReportWeekly,
  GetDependencyList,
  RecommenderDependencies,
  SaveScopeOfWork,
} from 'src/request/Projects';
import { useParams, useLocation } from 'react-router-dom';
import { getReport } from 'src/request/Reports';
import { ceilToDecimalPlaces } from 'src/utils/ceilToDecimalPlaces';
import useCheckPermission from 'src/utils/useCheckPermission';
import { showError } from 'src/utils/swalError';
import ProjectTabs from '../projects/ProjectTabs';
import queryString from 'query-string';
import { triggerProjectRefetch } from 'src/store/feature/project/projectSlice';
import { trackEvent, mixPanelEvents } from 'src/utils/mixpanel';
import { ProjectPowAmount } from './ProjectPowAmount';
import DownloadSwa from '../projects/DownloadSwa';
import DataFilter from 'src/reusable/DataFilter';




const formatDateToYYYYMMDD = (dateVal) => {
  if (!dateVal) return null;
  const d = new Date(dateVal);
  if (isNaN(d.getTime())) return null;
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const isNoneOption = (opt) =>
  Boolean(opt && (opt.value === 0 || opt.value === '0' || String(opt.value) === '0' || opt.label === 'None'));

export const handleDependencySelectionChange = (selected = [], currentTaskDeps = [], availableOptions = []) => {
  const noneOption = availableOptions.find(isNoneOption) || { label: 'None', risk_score: '0', value: 0 };
  if (!selected.length) return [noneOption];

  const realAvailable = availableOptions.filter((opt) => !isNoneOption(opt));
  const prevReal = currentTaskDeps.filter((opt) => !isNoneOption(opt));
  const selectedReal = selected.filter((opt) => !isNoneOption(opt));
  const selectedHasNone = selected.some(isNoneOption);
  const prevHadNone = !prevReal.length || currentTaskDeps.some(isNoneOption);

  if (selected.length === availableOptions.length && realAvailable.length) {
    return prevReal.length === realAvailable.length ? [noneOption] : realAvailable;
  }

  if ((selectedHasNone && !prevHadNone) || !selectedReal.length) {
    return [noneOption];
  }

  return selectedReal;
};

export const getMultiSelectValue = (taskDeps = [], availableOptions = []) => {
  const noneOption = availableOptions.find(isNoneOption) || { label: 'None', risk_score: '0', value: 0 };
  return !taskDeps.length || (taskDeps.length === 1 && isNoneOption(taskDeps[0]))
    ? [noneOption]
    : taskDeps.filter((opt) => !isNoneOption(opt));
};

export const customValueRenderer = (selected = []) => {
  const realSelected = selected.filter((opt) => !isNoneOption(opt));
  return realSelected.length ? realSelected.map((s) => s.label).join(', ') : 'None';
};

export const getPriorityColor = (priority) => {
  if (priority === 'high') return 'red';
  if (priority === 'moderate') return 'yellow';
  return 'green';
};

export const getStatusColor = (status) => {
  const numericStatus = Number(status);
  if (numericStatus >= 100) return 'green';
  if (numericStatus >= 1) return 'blue';
  return 'gray';
};

export const getRiskClassifierClass = (riskScore) => {
  const score = Number(riskScore);
  if (score <= 0.2) return 'float-left left-design-success';
  if (score <= 0.4) return 'float-left left-design-semiwarning';
  if (score <= 0.6) return 'float-left left-design-warning';
  if (score > 0.6) return 'float-left left-design-danger';
  return 'float-left left-design';
};

export const getChangedScopeOfWorkItems = (currentTaskItems, initialTaskItems) => {
  const initialMap = new Map();
  initialTaskItems?.forEach((phase) => {
    phase.Task?.forEach((t) => initialMap.set(t.id, t));
  });

  const changedItems = [];
  currentTaskItems?.forEach((phase) => {
    phase.Task?.forEach((task) => {
      const initialTask = initialMap.get(task.id);

      const currentStart = formatDateToYYYYMMDD(task.StartDate);
      const initialStart = formatDateToYYYYMMDD(initialTask?.StartDate);
      const startChanged = currentStart !== initialStart;

      const currentEnd = formatDateToYYYYMMDD(task.EndDate);
      const initialEnd = formatDateToYYYYMMDD(initialTask?.EndDate);
      const endChanged = currentEnd !== initialEnd;

      const currentDeps = (task.Dependencies || []).map((d) => Number(d.value)).sort((a, b) => a - b);
      const initialDeps = (initialTask?.Dependencies || []).map((d) => Number(d.value)).sort((a, b) => a - b);
      const depsChanged = JSON.stringify(currentDeps) !== JSON.stringify(initialDeps);

      const currentPowAmount = task.pow_amount ?? 0;
      const initialPowAmount = initialTask?.pow_amount ?? 0;
      const powAmountChanged = String(currentPowAmount) !== String(initialPowAmount);

      if (startChanged || endChanged || depsChanged || powAmountChanged) {
        const formattedDeps = (task.Dependencies || []).map((dep) => ({
          label: dep.label || '',
          value: dep.value,
          risk_score: String(dep.risk_score || '0'),
        }));

        changedItems.push({
          psow_id: task.id,
          phase_name: phase.Phase || '',
          scope_of_work: task.SOW || task.scope_of_work || '',
          start_date: startChanged ? currentStart : null,
          end_date: endChanged ? currentEnd : null,
          pow_amount: Number(currentPowAmount) || 0,
          dependencies: depsChanged ? formattedDeps : [],
        });
      }
    });
  });

  return changedItems;
};

export const ItemBadge = ({ underBudget, delayed, incomplete }) => {
  const badges = [];

  if (Number(underBudget) === 1) {
    badges.push(
      <CBadge key="budget" color="aimhi-success" className="badge-stats">
        WITHIN BUDGET
      </CBadge>,
    );
  } else {
    badges.push(
      <CBadge key="budget" color="aimhi-danger" className="badge-stats">
        OVER BUDGET
      </CBadge>,
    );
  }

  if (Number(incomplete) === 1) {
    badges.push(
      <CBadge key="status" color="aimhi-success" className="badge-stats">
        INCOMPLETE
      </CBadge>,
    );
  } else {
    if (Number(delayed) === 0) {
      badges.push(
        <CBadge key="status" color="aimhi-success" className="badge-stats" style={{ marginTop: '3px' }}>
          ON-TIME
        </CBadge>,
      );
    } else {
      badges.push(
        <CBadge key="status" color="aimhi-danger" className="badge-stats" style={{ marginTop: '3px' }}>
          DELAYED
        </CBadge>,
      );
    }
  }

  return <>{badges}</>;
};



export const TaskRowItem = ({
  task,
  taskIndex,
  phaseIndex,
  dependencies,
  isSaveDisabled,
  onPriorityChange,
  onAutoSave,
  onDateChange,
  onDependencyChange,
}) => {
  const priorityColor = getPriorityColor(task.Priority);
  const statusColor = getStatusColor(task.Status);
  const classifierClass = getRiskClassifierClass(task.risk_score);
  const availableDependencyOptions = useMemo(
    () => dependencies.filter((option) => option.value !== task.id),
    [dependencies, task.id],
  );

  return (
    <CCol
      xs="12"
      sm="12"
      md="12"
      lg="12"
      xl="12"
      key={`${task.sow_id}-${taskIndex}`}
      style={{ padding: '0px' }}
    >
      <div className={classifierClass}></div>
      <CCard id={`task-${task.sow_id}`} className="task-card" style={{ marginLeft: '30px' }}>
        <CRow>
          <CCol xs="12" sm="12" md="12" lg="3" id="Task">
            <span className="task-label">{task.SOW}</span>
          </CCol>

          <CCol xs="12" sm="12" md="12" lg="6" className="text-center" id="Priority, Status and dates">
            <CRow>
              <CCol xs="12" sm="12" md="12" lg="3" id="Priority" style={{ padding: '0px' }}>
                <span className="top-label">Priority</span>
                <br />
                <select
                  className={`${priorityColor} select-design ${priorityColor === 'yellow' ? 'text-dark' : ''}`}
                  onChange={(e) => onPriorityChange(e.target.value, taskIndex, phaseIndex)}
                  onBlur={(e) =>
                    onAutoSave(
                      {
                        id: task.id,
                        priority: e.target.value,
                      },
                      taskIndex,
                      phaseIndex,
                    )
                  }
                  value={task.Priority}
                  disabled={isSaveDisabled}
                >
                  <option style={{ backgroundColor: 'white' }} value=""></option>
                  <option style={{ backgroundColor: '#B42A19', color: 'white' }} value="high">
                    High
                  </option>
                  <option style={{ backgroundColor: '#FDFE54', color: 'black' }} value="moderate">
                    Moderate
                  </option>
                  <option style={{ backgroundColor: '#3A7D21', color: 'white' }} value="low">
                    Low
                  </option>
                </select>
              </CCol>

              <CCol xs="12" sm="12" md="12" lg="3" id="Status" style={{ padding: '0px' }}>
                <span className="top-label">Status</span>
                <br />
                <div className="progress-dropdown" style={{ marginTop: '3px' }}>
                  <div style={{ position: 'relative' }}>
                    <CProgress style={{ height: '15px' }}>
                      <div
                        id="editable"
                        style={{
                          position: 'absolute',
                          top: '50%',
                          left: '50%',
                          transform: 'translate(-50%, -50%)',
                          color: task.Status < 60 ? 'black' : 'white',
                          fontWeight: 'bold',
                        }}
                      >
                        <span>{task.Status}</span>%
                      </div>
                      <CProgressBar value={task.Status} className={`${statusColor} select-design`} />
                    </CProgress>
                  </div>
                </div>
              </CCol>

              <CCol xs="12" sm="12" md="12" lg="3" className="text-center" id="Target Start and Actual Start">
                <CRow>
                  <CCol xs="12" sm="12" md="12" lg="12" id="Target Start">
                    <span className="top-label">Target Start</span>
                    <br />
                    <DatePicker
                      className="border-2 border-black sow"
                      placeholderText="mm/dd/yyyy"
                      format="MM/dd/yyyy"
                      selected={task.StartDate ? new Date(task.StartDate) : null}
                      value={task.StartDate}
                      onChange={(value) => {
                        const dateObj = new Date(value);
                        const month = String(dateObj.getMonth() + 1).padStart(2, '0');
                        const day = String(dateObj.getDate()).padStart(2, '0');
                        const year = dateObj.getFullYear();
                        const formattedValue = `${month}/${day}/${year}`;
                        onDateChange(formattedValue, taskIndex, phaseIndex, 'itemtask.StartDate');
                      }}
                    />
                  </CCol>
                  <CCol xs="12" sm="12" md="12" lg="12" id="Actual Start">
                    <span className="top-label">Actual Start</span>
                    <br />
                    <span style={{ fontSize: '0.765625rem' }}>
                      {task.actual_start ? task.actual_start : 'mm/dd/yyyy'}
                    </span>
                  </CCol>
                </CRow>
              </CCol>

              <CCol xs="12" sm="12" md="12" lg="3" className="text-center" id="Target End and Actual End">
                <CRow>
                  <CCol xs="12" sm="12" md="12" lg="12" id="Target End">
                    <span className="top-label">Target End</span>
                    <br />
                    <DatePicker
                      className="border-2 border-black sow"
                      placeholderText="mm/dd/yyyy"
                      selected={task.EndDate ? new Date(task.EndDate) : null}
                      value={new Date(task.EndDate)}
                      onChange={(value) => {
                        const dateObj = new Date(value);
                        const month = String(dateObj.getMonth() + 1).padStart(2, '0');
                        const day = String(dateObj.getDate()).padStart(2, '0');
                        const year = dateObj.getFullYear();
                        const formattedValue = `${month}/${day}/${year}`;
                        onDateChange(formattedValue, taskIndex, phaseIndex, 'itemtask.EndDate');
                      }}
                    />
                  </CCol>
                  <CCol xs="12" sm="12" md="12" lg="12" id="Actual End">
                    <span className="top-label">Actual End</span>
                    <br />
                    <span style={{ fontSize: '0.765625rem' }}>
                      {task.actual_end ? task.actual_end : 'mm/dd/yyyy'}
                    </span>
                  </CCol>
                </CRow>
              </CCol>
            </CRow>
          </CCol>

          <CCol xs="12" sm="12" md="12" lg="3" className="text-center" id="Dependency and Dots">
            <CRow>
              <CCol xs="12" sm="12" md="12" lg="10" className="text-center" id="Dependency" style={{ paddingRight: '0px' }}>
                <CCol xs="12" sm="12" md="12" lg="12">
                  <span className="top-label">Dependency</span>
                  <br />
                  <MultiSelect
                    options={availableDependencyOptions}
                    value={getMultiSelectValue(task.Dependencies, availableDependencyOptions)}
                    onChange={(selected) => {
                      const updatedDeps = handleDependencySelectionChange(
                        selected,
                        task.Dependencies,
                        availableDependencyOptions,
                      );
                      onDependencyChange(updatedDeps, taskIndex, phaseIndex);
                    }}
                    valueRenderer={customValueRenderer}
                    labelledBy="Select"
                    className={task.recommender !== 1 ? 'multi-select-design' : 'multi-select-design-recommender'}
                    disabled={isSaveDisabled}
                    style={{ textAlign: 'left', border: '1px solid #ff8b67' }}
                  >
                    <div className="panel-content"></div>
                  </MultiSelect>
                </CCol>
              </CCol>
            </CRow>
          </CCol>
        </CRow>
      </CCard>
    </CCol>
  );
};

const PhaseCardItem = ({
  phaseItem,
  phaseIndex,
  isExpanded,
  dependencies,
  isSaveDisabled,
  onTogglePhase,
  onDragStart,
  onDragEnter,
  onDrop,
  onPriorityChange,
  onAutoSave,
  onDateChange,
  onDependencyChange,
}) => {
  const progressPercentage = ceilToDecimalPlaces(phaseItem.ProgressPercentage, 2);
  const totalBudget = ceilToDecimalPlaces(phaseItem.Total_Budget, 2);
  const totalCost = ceilToDecimalPlaces(phaseItem.Total_Cost, 2);

  return (
    <CCol
      xs="12"
      sm="12"
      md="12"
      lg="12"
      style={{ padding: '1px', marginTop: '20px' }}
      onDragStart={(e) => onDragStart(e, phaseIndex)}
      onDragEnter={(e) => onDragEnter(e, phaseIndex)}
      onDragEnd={onDrop}
      draggable
    >
      <CCard
        style={{ padding: '20px', marginBottom: '0' }}
        className="shadow-sm"
        onClick={() => onTogglePhase(phaseItem.Phase)}
      >
        <CRow>
          <CCol xs="12" sm="12" md="4" lg="3" xl="3" style={{ marginTop: '10px' }}>
            <span className="float-left circle-number">
              <span>{phaseIndex + 1}</span>
            </span>
            <div className="sow-label">{phaseItem.Phase?.toUpperCase()}</div>
          </CCol>

          <CCol xs="12" sm="12" md="1" lg="1" className="float-left text-center" style={{ paddingTop: '10px' }}>
            <CCol xs="12" sm="12" md="12" lg="12">
              <ItemBadge
                underBudget={phaseItem.under_budget}
                delayed={phaseItem.delayed}
                incomplete={phaseItem.incomplete}
              />
            </CCol>
          </CCol>

          <CCol md="7" lg="6" style={{ paddingLeft: '40px' }}>
            <CCol md="12" lg="12" className="float-right">
              <CRow>
                <CCol xs="6" sm="6" md="6" lg="6">
                  <span className="budget-label">Total Budget</span>
                  <div className="budget-value">
                    <div style={{ textAlign: 'left' }}>
                      ₱
                      <NumberFormat
                        value={totalBudget}
                        displayType="text"
                        thousandSeparator={true}
                        decimalScale={2}
                        fixedDecimalScale={true}
                        style={{ marginLeft: '5px' }}
                      />
                    </div>
                  </div>
                </CCol>
                <CCol xs="6" sm="6" md="6" lg="6">
                  <div className="float-right">
                    <div className="budget-label" style={{ textAlign: 'right' }}>
                      Total Cost
                    </div>
                    <div className="budget-value">
                      <div style={{ textAlign: 'right' }}>
                        ₱
                        <NumberFormat
                          value={totalCost}
                          displayType="text"
                          thousandSeparator={true}
                          decimalScale={2}
                          fixedDecimalScale={true}
                          style={{ marginLeft: '5px' }}
                        />
                      </div>
                    </div>
                  </div>
                </CCol>
              </CRow>
              <CProgress size="md">
                <CProgressBar
                  color="aimhi-textlight-neutral"
                  value={totalBudget === 0 ? 0 : totalCost}
                  max={totalBudget === 0 ? 1 : totalBudget}
                  showPercentage
                  className="text-white"
                />
              </CProgress>
            </CCol>
          </CCol>

          <CCol xs="12" sm="12" md="2" lg="2" className="align-center">
            <div className="circular-progress-size">
              <CircularProgressbarWithChildren
                className="circular-progress-size"
                value={progressPercentage}
                text={`${progressPercentage}%`}
                styles={{
                  path: {
                    stroke: '#351D75',
                    strokeLinecap: 'butt',
                    transition: 'stroke-dashoffset 0.5s ease 0s',
                    transformOrigin: 'center center',
                    circleRatio: '0',
                    strokeWidth: '-10',
                  },
                  trail: {
                    stroke: '#9c9c9c',
                    strokeLinecap: 'butt',
                    transform: 'rotate(0.25turn)',
                    transformOrigin: 'center center',
                    circleRatio: '0',
                    strokeWidth: '-10',
                  },
                  text: {
                    fill: '#23303D',
                    fontSize: '20px',
                    fontWeight: '700',
                  },
                  background: {
                    fill: '#54c2a2',
                  },
                }}
              />
            </div>
            <span className="status-label align-text-mobile">Progress</span>
          </CCol>
        </CRow>
      </CCard>

      <CCol xs="12" sm="12" md="12" lg="12" style={{ padding: '0px' }}>
        <div
          className={`transition-all duration-300 ${isExpanded ? 'opacity-100' : 'max-h-0 overflow-hidden opacity-0'
            }`}
        >
          {(phaseItem.Task || []).map((task, taskIndex) => (
            <TaskRowItem
              key={`${task.sow_id}-${taskIndex}`}
              task={task}
              taskIndex={task._originalTaskIndex ?? taskIndex}
              phaseIndex={phaseItem._originalPhaseIndex ?? phaseIndex}
              dependencies={dependencies}
              isSaveDisabled={isSaveDisabled}
              onPriorityChange={onPriorityChange}
              onAutoSave={onAutoSave}
              onDateChange={onDateChange}
              onDependencyChange={onDependencyChange}
            />
          ))}
        </div>
      </CCol>
    </CCol>
  );
};

const ProjectTaskHeaderActions = ({
  canDownloadReport,
  isDownloading,
  onOpenSwaModal,
  onSaveChanges,
  isSaving,
  showPowAmount,
  setShowPowAmount
}) => (
  <div className="flex items-center justify-between mt-5">
    <div className="flex items-center gap-3">
      <span className="text-sm font-medium text-[#23303D]">
        Construction Budget
      </span>

      <button
        type="button"
        role="switch"
        aria-checked={showPowAmount}
        onClick={() => setShowPowAmount((prev) => !prev)}
        className={`relative inline-flex h-6 w-11 shrink-0 cursor-pointer items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-[#23303D]/30 ${showPowAmount ? 'bg-[#23303D]' : 'bg-gray-300'
          }`}
      >
        <span
          className={`inline-block h-5 w-5 transform rounded-full bg-white shadow-sm transition-transform ${showPowAmount ? 'translate-x-5' : 'translate-x-0.5'
            }`}
        />
      </button>

      <span className="text-sm font-medium text-[#23303D]">
        POW Amount
      </span>
    </div>

    <div className="flex gap-[10px]">
      {canDownloadReport && (
        <CButton
          color="aimhi-textlight-neutral"
          variant="outline"
          size="sm"
          style={{ width: '155px' }}
          onClick={onOpenSwaModal}
          disabled={isDownloading}
        >
          {isDownloading ? (
            <>
              <CSpinner color="#23303D" size="sm" />
              <span style={{ marginLeft: '8px' }}>
                Download SWA
              </span>
            </>
          ) : (
            'Download SWA'
          )}
        </CButton>
      )}

      <CButton
        color="aimhi-textlight-neutral"
        size="sm"
        style={{ width: '155px', backgroundColor: '#23303D', color: '#fff' }}
        onClick={onSaveChanges}
        disabled={isSaving}
      >
        Save Changes
      </CButton>
    </div>
  </div>
);


const ProjectTask = () => {
  const accessToken = localStorage.getItem('token');
  const companyId = parseInt(localStorage.getItem('company_id'), 10);
  const { id: projectId } = useParams();
  const { search } = useLocation();

  const [loading, setLoading] = useState(true);
  const [taskItems, setTaskItems] = useState([]);
  const [initialTaskItems, setInitialTaskItems] = useState([]);
  const [isSaving, setIsSaving] = useState(false);
  const [expandedPhases, setExpandedPhases] = useState([]);
  const [dependencies, setDependencies] = useState([]);
  const [recommenderDependencies, setRecommenderDependenciesState] = useState([]);
  const [showRecommenderDependency, setShowRecommenderDependency] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);
  const [weeklyReportDate, setWeeklyReportDate] = useState('');
  const [searchParams, setSearchParams] = useState({});
  const [showPowAmount, setShowPowAmount] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [downloadModalVisible, setDownloadModalVisible] = useState(false);

  const reduxModule = useSelector((state) => state.module.module);
  const { openDialog, closeDialog } = useDialog();
  const { refreshSubscription, handleSubscriptionRequest, NoCreditsLeft } = useSubscriptionCheck();
  const checkPermission = useCheckPermission();
  const dispatch = useDispatch();

  const hasScrolled = useRef(false);
  const searchParamsRef = useRef({});
  const taskItemsRef = useRef([]);
  const dragItem = useRef(null);
  const dragOverItem = useRef(null);

  taskItemsRef.current = taskItems;

  const canSaveSow = !checkPermission('/sow/save');
  const canDownloadReportWeekly = checkPermission('/project/download/reports/weekly');

  const displayTaskItems = useMemo(() => {
    const term = searchTerm.toLowerCase().trim();

    if (!term) {
      return taskItems.map((phaseItem, phaseIdx) => ({
        ...phaseItem,
        _originalPhaseIndex: phaseIdx,
        Task: (phaseItem.Task || []).map((task, taskIdx) => ({
          ...task,
          _originalTaskIndex: taskIdx,
        })),
      }));
    }

    return taskItems
      .map((phaseItem, phaseIdx) => {
        const filteredTasks = (phaseItem.Task || [])
          .map((task, taskIdx) => ({
            ...task,
            _originalTaskIndex: taskIdx,
          }))
          .filter((task) => {
            const sowName = String(task.SOW || task.scope_of_work || '').toLowerCase();
            const phaseName = String(phaseItem.Phase || '').toLowerCase();

            return sowName.includes(term) || phaseName.includes(term);
          });

        return {
          ...phaseItem,
          _originalPhaseIndex: phaseIdx,
          Task: filteredTasks,
        };
      })
      .filter((phaseItem) => phaseItem.Task.length > 0);
  }, [taskItems, searchTerm]);

  const uniquePhases = useMemo(
    () => [...new Set(taskItems.map((item) => item.Phase))],
    [taskItems],
  );

  useEffect(() => {
    const parsed = queryString.parse(search);
    setSearchParams(parsed);
    searchParamsRef.current = parsed;
  }, [search]);

  useEffect(() => {
    hasScrolled.current = false;
    fetchDependencies(projectId);
    fetchRecommenderDependencies(projectId);
  }, [projectId]);

  useEffect(() => {
    fetchScopeOfWork(projectId);
  }, [projectId, search]);

  useEffect(() => {
    const phaseId = searchParamsRef.current?.phaseId;
    if (!phaseId || hasScrolled.current || loading) return;

    handleTogglePhase(phaseId, true, taskItems);

    const timer = setTimeout(() => {
      const element = document.getElementById(`task-${phaseId}`);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth', block: 'center', inline: 'center' });
        hasScrolled.current = true;
      }
    }, 400);

    return () => clearTimeout(timer);
  }, [loading]);

  useEffect(() => {
    if (reduxModule === 'project-scope-of-work') {
      const notificationService = new Notifications();
      notificationService.request_sow_notifications(companyId, projectId);
    }
  }, [reduxModule, companyId, projectId]);

  useEffect(() => {
    const fetchWeeklyReport = (status) => {
      getReport(projectId, 'weekly', status).then((res) => {
        if (res.status === 200) {
          setWeeklyReportDate(res.data.response.date);
        } else {
          console.error('error', res.data.response);
        }
      });
    };

    fetchWeeklyReport(0);
    requestSowNotifications(companyId, projectId);
  }, [companyId, projectId]);

  const handleTogglePhase = useCallback(
    (targetValue, isId = false, baseData = taskItemsRef.current) => {
      let resolvedPhase = targetValue;
      if (isId) {
        baseData.forEach((phase) => {
          const matchedTask = phase.Task?.find((task) => task.sow_id === parseInt(targetValue, 10));
          if (matchedTask) {
            resolvedPhase = phase.Phase;
          }
        });
      }

      setExpandedPhases((prevExpanded) => {
        const index = prevExpanded.indexOf(resolvedPhase);

        if (index >= 0) {
          const updated = [...prevExpanded];
          updated.splice(index, 1);
          return updated;
        }

        if (resolvedPhase === 'all') {
          applyRecommenderDependencies();
          return [...new Set(taskItemsRef.current.map((item) => item.Phase))];
        }

        return [...prevExpanded, resolvedPhase];
      });
    },
    [],
  );

  const fetchScopeOfWork = async (currentProjectId) => {
    const sowUrl = `${companyId}/${currentProjectId}`;
    const descUrl = `2?project_id=${currentProjectId}`;
    const orderBy = searchParamsRef.current?.order_by;
    const endpointUrl = orderBy === 'Description' ? descUrl : sowUrl;

    try {
      const res = await $axios.get(`${IP}sow/details/${endpointUrl}`, {
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${accessToken}`,
        },
      });

      const rawData = res?.data?.response || [];
      const formattedData = rawData.map((phase) => ({
        ...phase,
        Task: (phase.Task || []).map((task) => {
          const deps = task.Dependencies;
          const hasDeps = Array.isArray(deps) && deps.length > 0;
          return {
            ...task,
            Dependencies: hasDeps ? deps : [{ label: 'None', risk_score: '0', value: 0 }],
          };
        }),
      }));

      setTaskItems(formattedData);
      setInitialTaskItems(JSON.parse(JSON.stringify(formattedData)));
      setLoading(false);
    } catch (error) {
      if (error?.response?.status === 403) {
        showError(error?.response?.data?.response, 'Permission Denied');
        return;
      }
      console.error('projectdetails/ProjectOverview.js line 264', error);
    }
  };

  const fetchRecommenderDependencies = (currentProjectId) => {
    RecommenderDependencies(currentProjectId)
      .then((res) => {
        if (res.data.data.length > 0) {
          const updatedData = res.data.data.map((item) => ({
            ...item,
            dependencies: item.dependencies.map((dep) => ({
              label: dep.value === 0 ? 'None' : dep.label,
              value: dep.value === 0 ? '0' : dep.value,
            })),
          }));
          setRecommenderDependenciesState(updatedData);
          setShowRecommenderDependency(true);
        } else {
          setShowRecommenderDependency(true);
        }
      })
      .catch((error) => {
        console.error('error', error);
        setShowRecommenderDependency(false);
        if (error?.response?.status === 403) {
          showError(error?.response?.data?.response, 'Permission Denied');
        }
      });
  };

  const applyRecommenderDependencies = () => {
    setTaskItems((prevTasks) => {
      const copy = [...prevTasks];
      return copy.map((phase) => {
        phase.Task?.forEach((task) => {
          const matchingDependency = recommenderDependencies.find((dep) => dep.sow_id === task.sow_id);
          if (matchingDependency) {
            task.Dependencies = matchingDependency.dependencies;
            task.recommender = 1;
          }
        });
        return phase;
      });
    });
  };

  const fetchDependencies = (currentProjectId) => {
    const payload = {
      project_id: currentProjectId,
      company_id: companyId,
    };

    GetDependencyList(payload)
      .then((res) => {
        const uniqueValues = Array.from(new Set(res.data.response.map((dep) => dep.value)));
        const uniqueDependencyObjects = uniqueValues.map((value) =>
          res.data.response.find((dep) => dep.value === value),
        );
        setDependencies(uniqueDependencyObjects);
      })
      .catch((error) => {
        console.error('projectdetails/ProjectOverview.js line 281', error);
        if (error?.response?.status === 403) {
          showError(error?.response?.data?.response, 'Permission Denied');
        }
      });
  };

  const saveSowData = (data, taskIndex, phaseIndex, mixpanelSource) => {
    return $axios
      .post(`${IP}sow/save`, {
        data,
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${accessToken}`,
        },
      })
      .then((res) => {
        if (res?.data?.status === 1 && res?.data?.response === 2) {
          Swal.fire({
            title: 'Changes Submitted',
            icon: 'success',
            text: 'The changes were submitted successfully and are now pending approval.',
          });
        }

        trackEvent(mixpanelSource, {
          'Project ID': projectId,
          Source: mixpanelSource,
        });

        if (typeof data.dependencies !== 'undefined') {
          const arr = [...taskItemsRef.current];
          arr[phaseIndex].Task[taskIndex].Dependencies.push(...data.dependencies);
          setTaskItems(arr);
        }

        if (res.data.status === 1) {
          if (res.data.response === 1) {
            requestSowNotifications(companyId, projectId);
            fetchScopeOfWork(projectId);
            dispatch(triggerProjectRefetch(projectId));
          }
        } else {
          Swal.fire({
            icon: 'error',
            title: res.data.response,
            text: res.data.description,
            confirmButtonColor: '#23303D',
          });
          fetchScopeOfWork(projectId);
        }
      })
      .catch((error) => {
        console.error(error);
        if (error?.response?.status === 403) {
          showError(error?.response?.data?.response, 'Permission Denied');
        }
      });
  };

  const handleAutoSave = (data, taskIndex, phaseIndex) => {
    if (data.dependencies) {
      const includeNone = data.dependencies.filter((dependency) => dependency.value === 0);
      const excludeNone = data.dependencies.filter((dependency) => dependency.value !== 0);
      const lastIndex = data.dependencies.length - 1;

      if (excludeNone.length >= 1 && includeNone.length === 1) {
        if (data.dependencies[lastIndex].value === 0) {
          data.dependencies = includeNone;

          Swal.fire({
            title: 'WARNING',
            text: 'By selecting none option, your current selected dependencies will be removed.',
            icon: 'info',
            showCancelButton: true,
            confirmButtonText: 'Confirm',
            cancelButtonText: 'Cancel',
            confirmButtonColor: '#23303D',
            reverseButtons: true,
          }).then((result) => {
            if (result.isConfirmed) {
              saveSowData(data, taskIndex, phaseIndex, mixPanelEvents.SAVE_SOW);
            } else if (result.isDenied) {
              Swal.fire('Changes saved', '', 'info');
            }
          });
        } else {
          data.dependencies = excludeNone;
          saveSowData(data, taskIndex, phaseIndex, mixPanelEvents.SAVE_SOW);
        }
        return;
      }

      if (excludeNone.length <= 0 && includeNone.length >= 1) {
        data.dependencies = includeNone;
      } else if (excludeNone.length >= 1 && includeNone.length <= 0) {
        data.dependencies = excludeNone;
      }
    } else if (data.status || data.priority) {
      saveSowData(data, taskIndex, phaseIndex, mixPanelEvents.SAVE_SOW);
    }
  };

  const handlePowAmountChange = (value, taskIndex, phaseIndex) => {
    setTaskItems((prevItems) => {
      const newItems = [...prevItems];
      newItems[phaseIndex].Task[taskIndex].pow_amount = value;
      return newItems;
    });
  };

  const handlePriorityChange = (value, taskIndex, phaseIndex) => {
    setTaskItems((prevItems) => {
      const newItems = [...prevItems];
      newItems[phaseIndex].Task[taskIndex].Priority = value;
      return newItems;
    });
  };

  const handleDateChange = (formattedDate, taskIndex, phaseIndex, category) => {
    setTaskItems((prevItems) => {
      const newItems = [...prevItems];
      if (category === 'itemtask.StartDate') {
        newItems[phaseIndex].Task[taskIndex].StartDate = formattedDate;
      } else if (category === 'itemtask.EndDate') {
        newItems[phaseIndex].Task[taskIndex].EndDate = formattedDate;
      }
      return newItems;
    });
  };

  const handleDependencyChange = (updatedDeps, taskIndex, phaseIndex) => {
    setTaskItems((prevItems) => {
      const newItems = [...prevItems];
      newItems[phaseIndex].Task[taskIndex].Dependencies = updatedDeps;
      return newItems;
    });
  };

  const handleDeselectAll = () => {
    fetchScopeOfWork(projectId);
  };

  const handleAcceptRecommender = () => {
    const updatedTaskItems = [...taskItems];

    updatedTaskItems.forEach((phase, phaseIndex) => {
      phase.Task.forEach((task, taskIndex) => {
        handleAutoSave(
          {
            id: task.id,
            dependencies: task.Dependencies,
          },
          taskIndex,
          phaseIndex,
        );
      });
    });

    setTaskItems(updatedTaskItems);
  };

  const handleDragStart = (e, position) => {
    dragItem.current = position;
  };

  const handleDragEnter = (e, position) => {
    dragOverItem.current = position;
  };

  const handleDrop = () => {
    if (dragItem.current === null || dragOverItem.current === null) return;
    const reorderedList = [...taskItems];
    const dragItemContent = reorderedList[dragItem.current];
    reorderedList.splice(dragItem.current, 1);
    reorderedList.splice(dragOverItem.current, 0, dragItemContent);
    dragItem.current = null;
    dragOverItem.current = null;
    setTaskItems(reorderedList);
  };

  const handleSaveChanges = async () => {
    const changedItems = getChangedScopeOfWorkItems(taskItems, initialTaskItems);

    if (changedItems.length === 0) {
      Swal.fire({
        icon: 'info',
        title: 'No Changes',
        text: 'There are no changes to save.',
        confirmButtonColor: '#23303D',
      });
      return;
    }

    setIsSaving(true);
    const payload = {
      data: changedItems,
      project_id: parseInt(projectId, 10),
    };

    try {
      const res = await SaveScopeOfWork(payload);
      if (res?.data?.status === 1 || res?.status === 200) {
        Swal.fire({
          title: res?.data?.response === 2 ? 'Changes Submitted' : 'Success',
          icon: 'success',
          text:
            res?.data?.response === 2
              ? 'The changes were submitted successfully and are now pending approval.'
              : 'Scope of work changes saved successfully.',
          confirmButtonColor: '#23303D',
        });
        requestSowNotifications(companyId, projectId);
        fetchScopeOfWork(projectId);
        dispatch(triggerProjectRefetch(projectId));
      } else {
        Swal.fire({
          icon: 'error',
          title: res?.data?.response || 'Error',
          text: res?.data?.description || 'Failed to save changes.',
          confirmButtonColor: '#23303D',
        });
      }
    } catch (error) {
      console.error('Error saving scope of work:', error);
      if (error?.response?.status === 403) {
        showError(error?.response?.data?.response, 'Permission Denied');
      } else {
        Swal.fire({
          icon: 'error',
          title: 'Error',
          text: error?.response?.data?.message || 'Something went wrong while saving changes.',
          confirmButtonColor: '#23303D',
        });
      }
    } finally {
      setIsSaving(false);
    }
  };

  const handleDownloadReport = async (currentProjectId, type) => {
    setIsDownloading(true);
    const { balance, status } = await refreshSubscription();
    const [startDate, endDate] = weeklyReportDate.split(' - ').map((d) => d.trim());

    const proceedDownload = async () => {
      try {
        DownloadReportWeekly(currentProjectId, startDate, endDate, type);
        closeDialog();
      } catch (error) {
        if (error?.response?.status === 403) {
          showError(error?.response?.data?.response, 'Permission Denied');
          return;
        }
        Swal.fire({
          title: 'Oopss',
          icon: 'error',
          text: 'Something went wrong. Please try again',
        });
      } finally {
        setIsDownloading(false);
      }
    };

    if (status === 0) {
      if (balance != null && Number(balance) < 15) {
        setIsDownloading(false);
        NoCreditsLeft();
        return;
      }

      openDialog({
        title: 'Power Tool Feature',
        message: `You have ${balance} credits. Get unlimited Power Tools for P250/month or pay P15 per use. Once you use your credits, there's no going back—no refunds or changes. Are you sure you want to continue? If so, how would you like to proceed?`,
        onPrimaryAction: () => proceedDownload(),
        primaryActionText: 'Pay P15',
        onSecondaryAction: () =>
          handleSubscriptionRequest()
            .then((res) => res === 1 && proceedDownload())
            .finally(() => setIsDownloading(false)),
        secondaryActionText: 'Subscribe for P250',
        iconType: 'info',
        iconColor: '#3B74BE',
        onClose: () => setIsDownloading(false),
      });
    } else {
      proceedDownload();
    }
  };

  return (
    <>
      {loading && <Loading />}
      <ProjectTabs />
      <CRow>
        <CCol xs="12" sm="12" md="9" lg="12" xl="9">
          <ProjectTaskHeaderActions
            canDownloadReport={canDownloadReportWeekly}
            isDownloading={isDownloading}
            onOpenSwaModal={() => setDownloadModalVisible(true)}
            onSaveChanges={handleSaveChanges}
            isSaving={isSaving}
            showPowAmount={showPowAmount}
            setShowPowAmount={setShowPowAmount}
          />
          <DownloadSwa
            visible={downloadModalVisible}
            onClose={() => setDownloadModalVisible(false)}
            projectId={projectId}
          />
          <div className="mt-4 mb-2">
            <DataFilter
              showFilterDropdown={false}
              onSearchChange={(query) => setSearchTerm(query)}
              searchPlaceholder="Search Scope of Work..."
              showSelectedChips={false}
            />
          </div>
          {showPowAmount ? (
            <>
              {displayTaskItems.map((phaseItem, phaseIndex) => (
                <ProjectPowAmount
                  key={phaseItem.Phase || phaseIndex}
                  phaseItem={phaseItem}
                  phaseIndex={phaseItem._originalPhaseIndex ?? phaseIndex}
                  isExpanded={expandedPhases.includes(phaseItem.Phase)}
                  dependencies={dependencies}
                  isSaveDisabled={!canSaveSow}
                  onTogglePhase={handleTogglePhase}
                  onDragStart={handleDragStart}
                  onDragEnter={handleDragEnter}
                  onDrop={handleDrop}
                  onPriorityChange={handlePriorityChange}
                  onAutoSave={handleAutoSave}
                  onPowAmountChange={handlePowAmountChange}
                  onDateChange={handleDateChange}
                  onDependencyChange={handleDependencyChange}
                />
              ))}
            </>
          ) : (
            <>
              {displayTaskItems.map((phaseItem, phaseIndex) => (
                <PhaseCardItem
                  key={phaseItem.Phase || phaseIndex}
                  phaseItem={phaseItem}
                  phaseIndex={phaseItem._originalPhaseIndex ?? phaseIndex}
                  isExpanded={expandedPhases.includes(phaseItem.Phase)}
                  dependencies={dependencies}
                  isSaveDisabled={!canSaveSow}
                  onTogglePhase={handleTogglePhase}
                  onDragStart={handleDragStart}
                  onDragEnter={handleDragEnter}
                  onDrop={handleDrop}
                  onPriorityChange={handlePriorityChange}
                  onAutoSave={handleAutoSave}
                  onDateChange={handleDateChange}
                  onDependencyChange={handleDependencyChange}
                />
              ))}
            </>
          )}
        </CCol>
        <CCol xs="12" sm="12" md="3" lg="12" xl="3">
          <ThingsToConsider
            project_id={projectId}
            show_all={handleTogglePhase}
            onDecline={handleDeselectAll}
            onAccept={handleAcceptRecommender}
            showRecommenderDependency={showRecommenderDependency}
            taskItem={taskItems}
            sowOrderBy={searchParams?.order_by}
          />
        </CCol>
      </CRow >
    </>
  );
};

export default ProjectTask;